import React, { useEffect, useState } from "react";
import {
    Badge, Button, CircularProgress, Grid, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Tooltip, Typography, useTheme
} from "@mui/material";
import { connect } from "react-redux";
//--------------------------|| 3RD PARTY LIBRARIES ||------------------------------------//
import {
    IconFilter,
    IconGridDots,
    IconPlaylistAdd,
    IconSortAscending2,
    IconSortDescending2,
} from "@tabler/icons-react";
import moment from "moment";
//--------------------------|| FILES IMPORT ||------------------------------------------//
import { getPager } from "helper/common";
import MainCard from "ui-component/cards/MainCard";
import SubCard from "ui-component/cards/SubCard";
import { NoData } from "ui-component/custom-components/nodata";
import { VWPagination } from "ui-component/custom-components/pagination";
import { ErrorAlert } from "ui-component/custom-components/alertMessage";
import Chip from "ui-component/extended/Chip";
import CreateRegion from "./create";
import FilterRegion from "./filter";
import {
    clearStateList,
    closeStateFilterDialog,
    closeStateCreateDialog,
    getStateList,
    openStateFilterDialog,
    openStateCreateDialog,
    setStateFilteredData,
    stateListErrorClear
} from "store/app/form-management/state-configuration/actions";
import { Link } from "react-router-dom";

const tableHeaderStyle = { paddingLeft: '0px' }

const StateList = (props) => {
    //--------------------List--------------------------------------
    const { stateList, totalCounts, isPending, errorMessage } = props;

    const [pageSize, setPageSize] = useState(10);
    const [Pager, setPager] = useState({ currentPage: 1 });
    const [sortDirection, setSortDirection] = useState('asc');
    const [sortOrder, setSortOrder] = useState(null);

    //--------------------Filter------------------------------------
    const { stateFilterData, isStateFilterDialogOpen } = props;
    //--------------------Create------------------------------------
    const { isStateCreateDialogOpen } = props;

    //--------------------Profile-----------------------------------
    const { userProfile } = props;
    const { userRoleName } = userProfile || {};

    //--------------------UI Design---------------------------------
    const theme = useTheme();
    const colorPrimary = theme.palette.secondary.dark;
    const [tableHeight, setTableHeight] = useState(window.innerHeight);

    //--------------------------------------------------------------
    useEffect(() => {
        props.stateListErrorClear();

        const handleResize = () => {
            setTableHeight(window.innerHeight)
        };
        window.addEventListener("resize", handleResize);
        return () => {
            props.clearStateList();
            window.removeEventListener("resize", handleResize);
        }
    }, [])

    //--------------------------|| SORTING ||------------------------------------------//
    const handleSort = (column) => {
        if (sortOrder === column) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        }
        else {
            setSortOrder(column);
            setSortDirection('asc');
        }
    };

    //--------------------------|| PAGINATION ||------------------------------------------//
    const setPage = (page = 1) => {
        props.getStateList(page, pageSize, sortOrder, sortDirection, stateFilterData);

        setPager(prevPager => {
            const totalPages = Math.ceil(totalCounts / pageSize);
            const visiblePages = 4;
            const pageOffset = Math.max(0, Math.floor((page - 1) / visiblePages) * visiblePages);
            const newPages = [];
            for (let i = pageOffset + 1; i <= Math.min(totalPages, pageOffset + visiblePages); i++) {
                newPages.push(i);
            }
            return {
                ...prevPager,
                currentPage: page,
                totalPages,
                pages: newPages,
            }
        })
    }

    useEffect(() => {
        const currentPager = getPager(totalCounts, 1, pageSize);
        setPager(currentPager)
    }, [totalCounts]);

    useEffect(() => {
        setPage();
    }, [stateFilterData, sortOrder, sortDirection, pageSize])

    //--------------------------|| FILTER ||------------------------------------------//
    const handleFilterOpen = () => {
        props.openStateFilterDialog();
    }
    const handleFilterClose = () => {
        props.closeStateFilterDialog();
    }
    //--------------------------|| CREATE ||------------------------------------------//
    const handleRegionCreateOpen = () => {
        props.openStateCreateDialog();
    }
    const handleRegionCreateClose = () => {
        props.closeStateCreateDialog();
    }

    return (
        <>
            {/*----------------------------|| HEADER ||--------------------------------*/}
            <SubCard sx={{
                p: 1.5,
                pl: 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}
            >
                <Grid item sx={{ display: 'flex', gap: { lg: 2, md: 2, sm: 1, xs: 1 }, alignItems: "center", marginRight: "10px" }}>
                    <Typography variant="h4">State/Export List - </Typography>
                    <Tooltip title="State/Export Count" placement="top">
                        <span>
                            <Chip
                                label={totalCounts}
                                chipcolor="secondary"
                                size="small"
                                sx={{
                                    borderRadius: "20%",
                                    py: 1,
                                    fontWeight: "bold",
                                    fontSize: 15,
                                    cursor: "pointer"
                                }}
                            />
                        </span>
                    </Tooltip>
                </Grid>
                <Grid item sx={{ display: 'flex', gap: 2 }}>
                    <Badge
                        badgeContent={stateFilterData ? Object.keys(stateFilterData).length : 0}
                        color="secondary"
                        invisible={stateFilterData ? Object.keys(stateFilterData).length === 0 : true}
                    >
                        <Button
                            variant="contained"
                            size="small"
                            onClick={handleFilterOpen}
                            startIcon={<IconFilter />}
                        >
                            Filter
                        </Button>
                    </Badge>
                    {(userRoleName === "Administrator" || userRoleName === "Master admin") &&
                        <Button
                            variant="contained"
                            size="small"
                            onClick={handleRegionCreateOpen}
                            startIcon={<IconPlaylistAdd />}
                        >
                            Create State/Export
                        </Button>
                    }
                </Grid>
            </SubCard>
            {/*----------------------------|| TABLE ||--------------------------------*/}
            <MainCard sx={{ marginTop: '15px' }}>
                {errorMessage &&
                    <ErrorAlert type={'error'} content={errorMessage} />
                }
                <TableContainer sx={{ whiteSpace: "nowrap", maxHeight: `calc(${tableHeight}px - 380px)`, overflow: "auto" }}>
                    <Table size="small" stickyHeader aria-label="sticky table"
                        sx={{
                            '& tr > *:last-child': {
                                position: 'sticky',
                                right: 0,
                                bgcolor: 'white',
                            },
                        }}
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    <Button sx={tableHeaderStyle}
                                        onClick={() => handleSort('StateName')}
                                        endIcon={sortOrder === 'StateName' && (
                                            sortDirection === 'asc' ?
                                                <IconSortAscending2 color={colorPrimary} />
                                                : <IconSortDescending2 color={colorPrimary} />
                                        )}
                                    >
                                        State/Export Name
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    <Button sx={tableHeaderStyle}
                                        onClick={() => handleSort('RegionName')}
                                        endIcon={sortOrder === 'RegionName' && (
                                            sortDirection === 'asc' ?
                                                <IconSortAscending2 color={colorPrimary} />
                                                : <IconSortDescending2 color={colorPrimary} />
                                        )}
                                    >
                                        Region Name
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    <Button
                                        sx={tableHeaderStyle}
                                        onClick={() => handleSort('AssignedUserCount')}
                                        endIcon={sortOrder === 'AssignedUserCount' && (
                                            sortDirection === 'asc' ?
                                                <IconSortAscending2 color={colorPrimary} />
                                                : <IconSortDescending2 color={colorPrimary} />
                                        )}
                                    >
                                        Assigned Users Count
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    <Button
                                        sx={tableHeaderStyle}
                                        onClick={() => handleSort('IsActive')}
                                        endIcon={sortOrder === 'IsActive' && (
                                            sortDirection === 'asc' ?
                                                <IconSortAscending2 color={colorPrimary} />
                                                : <IconSortDescending2 color={colorPrimary} />
                                        )}
                                    >
                                        Active ?
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    <Button
                                        sx={tableHeaderStyle}
                                        onClick={() => handleSort('CreatedBy')}
                                        endIcon={sortOrder === 'CreatedBy' && (
                                            sortDirection === 'asc' ?
                                                <IconSortAscending2 color={colorPrimary} />
                                                : <IconSortDescending2 color={colorPrimary} />
                                        )}
                                    >
                                        Created By
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    View
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(stateList?.length > 0) && !isPending ?
                                stateList?.map((data, index) => (
                                    <TableRow key={index}>
                                        <TableCell>{data.StateName || 'N/A'}</TableCell>
                                        <TableCell>{data.RegionName || 'N/A'}</TableCell>
                                        <TableCell>{data.AssignedUserCount || 0}</TableCell>
                                        <TableCell>
                                            {(data.IsActive ?
                                                <Chip label={"Active"} chipcolor="success" size="small" />
                                                : <Chip label={"Inactive"} chipcolor="error" size="small" />
                                            )}
                                        </TableCell>
                                        <TableCell>{data.CreatedBy || 'N/A'}<br />
                                            <small>{data.CreatedTimeStamp ? moment(data.CreatedTimeStamp).format("DD MMM YYYY") : "N/A"}</small>
                                        </TableCell>
                                        <TableCell>
                                            <Link to={`/state-configuration/details/${data.Id}`}>
                                                <IconGridDots color={theme.palette.primary.main} />
                                            </Link>
                                        </TableCell>
                                    </TableRow>
                                ))
                                /*--------|| LOADING ||---------*/
                                : isPending ?
                                    <TableRow >
                                        <TableCell colSpan={8} height='300px'>
                                            <Grid container alignItems={"center"} justifyContent={"center"} gap={1}
                                                sx={{
                                                    position: "sticky",
                                                    width: "fit-content",
                                                    top: "50%",
                                                    left: "50%",
                                                    transform: "translate(-50%, -50%)"
                                                }}
                                            >
                                                <CircularProgress />
                                                <Typography variant="h5" color={'primary'}> Loading...</Typography>
                                            </Grid>
                                        </TableCell>
                                    </TableRow>
                                    /*--------|| NO DATA ||---------*/
                                    : <TableCell colSpan={8} height='300px'>
                                        <NoData />
                                    </TableCell>
                            }
                        </TableBody>
                    </Table>
                </TableContainer>
                {/*--------|| PAGINATION ||---------*/}
                <Grid>
                    <VWPagination
                        pageDetails={Pager}
                        pageChange={setPage}
                        totalRecords={totalCounts}
                        pageSize={pageSize}
                        setPageSize={setPageSize}
                    />
                </Grid>
            </MainCard>
            <FilterRegion
                open={isStateFilterDialogOpen}
                onClose={handleFilterClose}
                setPage={setPage}
            />
            <CreateRegion
                filter={stateFilterData}
                paginationData={{
                    page: (Pager && Pager.currentPage) || 1,
                    pageSize: pageSize,
                    sortDirection: sortDirection,
                    sortOrder: sortOrder
                }}
                open={isStateCreateDialogOpen}
                onClose={handleRegionCreateClose}
            />
        </>
    )
}

const mapStateToProps = (state) => ({
    //--------------------List--------------------------------------
    stateList: state.states.stateList,
    totalCounts: state.states.stateListCount,
    isPending: state.states.isStateListPending,
    errorMessage: state.states.stateListErrorMessage,
    //--------------------Filter------------------------------------
    isStateFilterDialogOpen: state.states.isStateFilterDialogOpen,
    stateFilterData: state.states.stateFilterData,
    //--------------------Create------------------------------------
    isStateCreateDialogOpen: state.states.isStateCreateDialogOpen,
    //--------------------Profile-----------------------------------
    userProfile: state.login.userProfile,
})

const mapDispatchToProps = (dispatch) => ({
    //--------------------List--------------------------------------
    getStateList: (page, pageSize, sortOrder, sortDirection, filter) => dispatch(getStateList(page, pageSize, sortOrder, sortDirection, filter)),
    clearStateList: () => dispatch(clearStateList()),
    stateListErrorClear: () => dispatch(stateListErrorClear()),
    //--------------------Filter------------------------------------
    openStateFilterDialog: () => dispatch(openStateFilterDialog()),
    closeStateFilterDialog: () => dispatch(closeStateFilterDialog()),
    setStateFilteredData: (filter) => dispatch(setStateFilteredData(filter)),
    //--------------------Create------------------------------------
    openStateCreateDialog: () => dispatch(openStateCreateDialog()),
    closeStateCreateDialog: () => dispatch(closeStateCreateDialog()),
})
export default connect(mapStateToProps, mapDispatchToProps)(StateList);